/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-toastify";
import { usePlayersAdmin } from "@/hooks/api/usePlayers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { eventKeys } from "@/hooks/api/useEvents";
import type { Match, Player } from "@/types/api.types";

interface BulkAppearanceFormProps {
  match: Match;
  onClose: () => void;
  /** starters mode: send minutesPlayed=1 → only APPEARANCE (+1pt), no 60+ bonus yet */
  startersMode?: boolean;
  /**
   * Current live match minute (from useMatchTimer in the parent).
   * When provided and < 60, submitting any "Full" (60+ min) appearance triggers a
   * confirm dialog so admin doesn't accidentally award the long-appearance bonus
   * before it's physically possible.
   */
  currentMatchMinute?: number;
}

interface AppearanceEntry {
  playerId: number;
  name: string;
  position: string;
  nation: string;
  jerseyNumber: number | null;
  minutesPlayed: number;
  selected: boolean;
}

const POSITION_ORDER = ["GK", "DEF", "MID", "FWD"];
const POSITION_COLORS: Record<string, string> = {
  GK: "bg-yellow-100 text-yellow-800",
  DEF: "bg-blue-100 text-blue-800",
  MID: "bg-green-100 text-green-800",
  FWD: "bg-red-100 text-red-800",
};

export default function BulkAppearanceForm({
  match,
  onClose,
  startersMode = false,
  currentMatchMinute,
}: BulkAppearanceFormProps) {
  const [entries, setEntries] = useState<AppearanceEntry[]>([]);
  const queryClient = useQueryClient();

  const { data: homePlayers, isLoading: homeLoading, error: homeError } =
    usePlayersAdmin({ nationId: match.homeNationId, isActive: true, limit: 30 });

  const { data: awayPlayers, isLoading: awayLoading, error: awayError } =
    usePlayersAdmin({ nationId: match.awayNationId, isActive: true, limit: 30 });

  useEffect(() => {
    const home = homePlayers?.players || [];
    const away = awayPlayers?.players || [];
    const allPlayers = [...home, ...away];
    if (allPlayers.length === 0) return;

    setEntries(
      allPlayers.map((p: Player) => ({
        playerId: p.id,
        name: p.name,
        position: p.position,
        nation: typeof p.nation === "string" ? p.nation : (p.nation?.name || ""),
        jerseyNumber: p.jerseyNumber ?? null,
        // starters mode: send as < 60 so only APPEARANCE created (+1pt)
        // fulltime mode: default to Full (90min) for +2pts
        minutesPlayed: startersMode ? 1 : 90,
        selected: true,
      }))
    );
  }, [homePlayers, awayPlayers, startersMode]);

  const { mutate: submitBulk, isPending } = useMutation({
    mutationFn: (data: {
      matchId: number;
      appearances: { playerId: number; minutesPlayed: number }[];
    }) => apiClient.post(API_ENDPOINTS.EVENTS.BULK_APPEARANCES, data),
    onSuccess: (res: any) => {
      const count = res.results?.length || 0;
      toast.success(
        startersMode
          ? `👟 Starting 11 logged — ${count} players got +1pt`
          : `👟 Appearances logged — ${count} players updated`
      );
      queryClient.invalidateQueries({ queryKey: eventKeys.match(match.id) });
      onClose();
    },
    onError: (error: any) =>
      toast.error(error.response?.data?.message || "Failed to log appearances"),
  });

  const updateEntry = (index: number, field: keyof AppearanceEntry, value: any) => {
    setEntries((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [field]: value } : e))
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setEntries((prev) => prev.map((e) => ({ ...e, selected })));
  };

  const handleSubmit = () => {
    const selected = entries.filter((e) => e.selected);
    if (selected.length === 0) {
      toast.error("Select at least one player");
      return;
    }

    // Guard against logging a 60+ min ("long") appearance before the match has
    // physically reached minute 60. Bypass entirely in starters mode (all 1 min)
    // and when the parent doesn't pass a current minute (defensive — old callers).
    if (!startersMode && typeof currentMatchMinute === "number" && currentMatchMinute < 60) {
      const longApps = selected.filter((e) => e.minutesPlayed >= 60);
      if (longApps.length > 0) {
        const ok = confirm(
          `⚠️ Match is only at minute ${currentMatchMinute}.\n\n` +
            `${longApps.length} player(s) are marked "Full" (60+ min long appearance, +2pts each). ` +
            `That bonus can only be earned after minute 60 of real play.\n\n` +
            `Continue and log these long appearances anyway?`,
        );
        if (!ok) return;
      }
    }

    submitBulk({
      matchId: match.id,
      appearances: selected.map((e) => ({
        playerId: e.playerId,
        minutesPlayed: e.minutesPlayed,
      })),
    });
  };

  const selectedCount = entries.filter((e) => e.selected).length;
  const isEarlyMatch =
    !startersMode &&
    typeof currentMatchMinute === "number" &&
    currentMatchMinute < 60;
  const earlyLongCount = isEarlyMatch
    ? entries.filter((e) => e.selected && e.minutesPlayed >= 60).length
    : 0;

  const homeEntries = entries
    .map((e, i) => ({ entry: e, index: i }))
    .filter((item) => item.entry.nation === match.homeNation.name)
    .sort((a, b) => POSITION_ORDER.indexOf(a.entry.position) - POSITION_ORDER.indexOf(b.entry.position));

  const awayEntries = entries
    .map((e, i) => ({ entry: e, index: i }))
    .filter((item) => item.entry.nation === match.awayNation.name)
    .sort((a, b) => POSITION_ORDER.indexOf(a.entry.position) - POSITION_ORDER.indexOf(b.entry.position));

  const renderPlayerRow = (entry: AppearanceEntry, globalIndex: number) => {
    const pointsPreview = startersMode ? 1 : entry.minutesPlayed >= 60 ? 2 : 1;

    return (
      <div
        key={entry.playerId}
        className={`flex items-center gap-2 p-2 rounded-lg transition ${
          entry.selected ? "bg-white" : "bg-gray-50 opacity-50"
        }`}
      >
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={entry.selected}
          onChange={(e) => updateEntry(globalIndex, "selected", e.target.checked)}
          className="w-4 h-4 accent-blue-600 cursor-pointer shrink-0"
        />

        {/* Position */}
        <Badge className={`text-xs w-9 justify-center shrink-0 ${POSITION_COLORS[entry.position]}`}>
          {entry.position}
        </Badge>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{entry.name}</p>
          {entry.jerseyNumber && (
            <p className="text-xs text-gray-400">#{entry.jerseyNumber}</p>
          )}
        </div>

        {/* Sub / Full toggle — only in fulltime mode */}
        {!startersMode && (
          <div className="flex gap-1 shrink-0">
            <button
              type="button"
              disabled={!entry.selected}
              onClick={() => updateEntry(globalIndex, "minutesPlayed", 45)}
              className={`px-2 py-1 rounded text-xs font-medium border transition-colors disabled:opacity-40 ${
                entry.selected && entry.minutesPlayed < 60
                  ? "bg-orange-100 border-orange-400 text-orange-800"
                  : "bg-white border-gray-200 text-gray-400 hover:border-gray-300"
              }`}
              title="Sub — less than 60 min (+1pt)"
            >
              Sub
            </button>
            <button
              type="button"
              disabled={!entry.selected}
              onClick={() => updateEntry(globalIndex, "minutesPlayed", 90)}
              className={`px-2 py-1 rounded text-xs font-medium border transition-colors disabled:opacity-40 ${
                entry.selected && entry.minutesPlayed >= 60
                  ? "bg-violet-600 border-violet-600 text-white"
                  : "bg-white border-gray-200 text-gray-400 hover:border-gray-300"
              }`}
              title="Full / 60+ min (+2pts)"
            >
              Full
            </button>
          </div>
        )}

        {/* Points preview */}
        <span className={`text-xs font-bold w-8 text-right shrink-0 ${
          pointsPreview >= 2 ? "text-violet-600" : "text-indigo-600"
        }`}>
          +{pointsPreview}pt
        </span>
      </div>
    );
  };

  if (homeLoading || awayLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-8 space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="text-sm text-gray-500">Loading players…</p>
      </div>
    );
  }

  if (homeError || awayError) {
    return (
      <div className="flex flex-col justify-center items-center py-8 space-y-2">
        <p className="text-sm text-red-500">Error loading players</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-8 space-y-3 text-center">
        <div className="text-4xl">⚠️</div>
        <p className="text-sm font-semibold text-gray-700">No Players Found</p>
        <p className="text-xs text-gray-500 max-w-md">
          No active players found for {match.homeNation.name} or {match.awayNation.name}.
        </p>
        <Button onClick={onClose} variant="outline" size="sm">Close</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-800">
            {startersMode ? "👟 Log Starting 11" : "👟 Log Appearances"}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {startersMode
              ? "Select who is starting. Each gets +1pt immediately. Award +1pt 60-min bonus at full time."
              : "Mark who played. Sub = <60 min (+1pt). Full = 60+ min (+2pts total)."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" className="text-xs text-blue-600 h-7"
            onClick={() => handleSelectAll(true)}>
            Select All
          </Button>
          <Button type="button" variant="ghost" size="sm" className="text-xs text-gray-500 h-7"
            onClick={() => handleSelectAll(false)}>
            Clear
          </Button>
        </div>
      </div>

      {/* Early-match warning — appears when match minute < 60 and any "Full" selected */}
      {isEarlyMatch && earlyLongCount > 0 && (
        <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-300 rounded-lg p-2.5 text-amber-900">
          <span className="text-base leading-none">⚠️</span>
          <div className="flex-1">
            <p className="font-bold">
              Match is at minute {currentMatchMinute} — too early for 60+ min bonus
            </p>
            <p className="mt-0.5 opacity-90">
              {earlyLongCount} player{earlyLongCount === 1 ? " is" : "s are"} marked
              "Full" ({" "}
              <span className="font-mono">+2pts</span>
              ). A 60+ min long appearance can only be earned after minute 60 of real
              play. You'll be asked to confirm on submit.
            </p>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex gap-3 text-xs bg-white rounded-lg p-2 border">
        {startersMode ? (
          <>
            <span className="text-indigo-600 font-medium">👟 All starters = +1pt each (APPEARANCE)</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-400">Use "Log Appearances" at full time to award 60+ min bonus</span>
          </>
        ) : (
          <>
            <span className="text-orange-700 font-medium">Sub = +1pt (&lt;60 min)</span>
            <span className="text-gray-300">|</span>
            <span className="text-violet-700 font-medium">Full = +2pts (60+ min)</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-400">Uncheck players who didn't play</span>
          </>
        )}
      </div>

      {/* Two-column player list */}
      <div className="grid grid-cols-2 gap-4 max-h-80 overflow-y-auto">
        {/* Home */}
        <div>
          <div className="flex items-center gap-2 mb-1.5 sticky top-0 bg-indigo-50 py-1 px-2 rounded">
            {match.homeNation.flagUrl && (
              <img src={match.homeNation.flagUrl} className="w-5 h-3.5 object-cover rounded" alt="" />
            )}
            <p className="text-xs font-bold text-gray-700">{match.homeNation.name}</p>
          </div>
          <div className="space-y-1">
            {homeEntries.map((item) => renderPlayerRow(item.entry, item.index))}
          </div>
        </div>

        {/* Away */}
        <div>
          <div className="flex items-center gap-2 mb-1.5 sticky top-0 bg-indigo-50 py-1 px-2 rounded">
            {match.awayNation.flagUrl && (
              <img src={match.awayNation.flagUrl} className="w-5 h-3.5 object-cover rounded" alt="" />
            )}
            <p className="text-xs font-bold text-gray-700">{match.awayNation.name}</p>
          </div>
          <div className="space-y-1">
            {awayEntries.map((item) => renderPlayerRow(item.entry, item.index))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t">
        <p className="text-sm text-gray-500">
          {selectedCount} of {entries.length} players selected
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || selectedCount === 0}
            className={startersMode
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-blue-600 hover:bg-blue-700 text-white"}
          >
            {isPending
              ? "Submitting…"
              : startersMode
                ? `✅ Confirm Starting 11 (${selectedCount} players, +1pt each)`
                : `Submit Appearances (${selectedCount} players)`}
          </Button>
        </div>
      </div>
    </div>
  );
}
